import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import api from '../../../api/axios';
import { showSuccess, showError } from '../../../utils/toast';
import { useLoading } from '../../../components/LoadingProvider';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

function DoctorReports() {
    const navigate = useNavigate();
    const MySwal = withReactContent(Swal);
    const { setIsLoading } = useLoading();
    const { user, userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['doctor'] });
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    const [value, setValue] = useState([thirtyDaysAgo, today]);
    const [earningsData, setEarningsData] = useState({
        total_earnings: 0,
        weekly_earnings: [],
        weekly_total_earnings: 0,
        today_earnings: 0,
        filtered_earnings: [],
        total_withdrawals: 0,
        last_7_withdrawals: [],
    });
    const [withdrawalFormData, setWithdrawalFormData] = useState({
        withdrawal_amount: 0,
        withdrawal_payment_mode: ''
    });
    const [withdrawalFormError, setWithdrawalFormError] = useState({
        withdrawal_amount: '',
        withdrawal_payment_mode: ''
    });
    const forteenDaysAgo = new Date(today);
    forteenDaysAgo.setDate(today.getDate() - 14);
    const [selectDate, setSelectDate] = useState([forteenDaysAgo, today]);

    useEffect(() => {
        document.title = 'Varosa - Doctor Reports';
        if (!user) {
            navigate('/login');
            return;
        }
        fetchEarningsData();
    }, [user, navigate, selectDate]);
    const fetchEarningsData = async () => {
        try {
            setIsLoading(true);
            let date = selectDate?.map((item) => item?.toLocaleDateString());
            const response = await api.get('/'+userRole+'/earnings-report', {
                params: {
                    start_date: date[0],
                    end_date: date[1],
                },
            });
            if (response?.data?.result === 'success') {
                setEarningsData({
                    total_earnings: response?.data?.data?.total_earnings || 0.00,
                    weekly_earnings: response?.data?.data?.weekly_earnings || [],
                    weekly_total_earnings: response?.data?.data?.weekly_total_earnings || 0.00,
                    today_earnings: response?.data?.data?.today_earnings || 0.00,
                    filtered_earnings: response?.data?.data?.filtered_earnings || [],
                    total_withdrawals: response?.data?.data?.total_withdrawals || 0.00,
                    last_7_withdrawals: response?.data?.data?.last_7_withdrawals || [],
                });
                setWithdrawalFormData({
                    withdrawal_amount: (response?.data?.data?.total_earnings || 0.00) - (response?.data?.data?.total_withdrawals || 0.00),
                    withdrawal_payment_mode: 'Cash'
                });
                setWithdrawalFormError({
                    withdrawal_amount: '',
                    withdrawal_payment_mode: ''
                });
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
            if (error.response?.status === 401) {
                navigate('/login');
            }
        } finally {
            setIsLoading(false);
        }
    };
    const viewFileViewFinder = async (url) => {
        MySwal.fire({
            title: 'File View Finder',
            html: `<iframe src="${url}" alt="File View Finder" style="width: 100%;height: 600px;"></iframe>`,
            width: '90%',
            showConfirmButton: true,
            confirmButtonText: 'Close',
            customClass: {
                container: '',
            },
        });
    };
    const handleWithdrawalRequest = async () => {
        try {
            setWithdrawalFormError({
                withdrawal_amount: '',
                withdrawal_payment_mode: ''
            });
            if (!withdrawalFormData?.withdrawal_amount) {
                setWithdrawalFormError({ ...withdrawalFormError, withdrawal_amount: 'Amount is required' });
                return;
            }
            if (!withdrawalFormData?.withdrawal_payment_mode) {
                setWithdrawalFormError({ ...withdrawalFormError, withdrawal_payment_mode: 'Payment mode is required' });
                return;
            }
            setIsLoading(true);
            const response = await api.post(`/${userRole}/payment-withdrawal-request`, withdrawalFormData);
            if (response?.data?.result === 'success') {
                showSuccess(response?.data?.message);
                fetchEarningsData();  
                return;
            } else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Error Payment Withdrawal Request:', error?.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            <div className="container py-5 login-container">
                <div className="row g-3">
                    <div className="col-12">
                        <h2>Reports</h2>
                    </div>
                </div>
            </div>
            <div className="container-fluid bg-light py-5" id="findDoctor">
                <div className="container">
                    <div className="section-title mb-5 wow fadeInUp" data-wow-delay="0.1s">
                        <div className="sub-style">
                            <h4 className="sub-title px-3 mb-0">My Earnings</h4>
                        </div>
                    </div>
                    <div className="row g-4 justify-content-center">
                        <div className="col-md-4 wow fadeInUp" data-wow-delay="0.1s">
                            <div className="row-cols-1 feature-item p-4 rounded" style={{border: '1px solid var(--bs-primary)'}}>
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <i className="fas fa-rupee-sign fa-4x text-primary"></i>
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">Total Earnings</h5>
                                        <h3 className="mb-3">Rs. {earningsData.total_earnings}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4 wow fadeInUp" data-wow-delay="0.1s">
                            <div className="row-cols-1 feature-item p-4 rounded" style={{border: '1px solid var(--bs-primary)'}}>
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <i className="fas fa-rupee-sign fa-4x text-primary"></i>
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">Weekly Earnings</h5>
                                        <h3 className="mb-3">Rs. {earningsData.weekly_total_earnings}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4 wow fadeInUp" data-wow-delay="0.1s">
                            <div className="row-cols-1 feature-item p-4 rounded" style={{border: '1px solid var(--bs-primary)'}}>
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <i className="fas fa-rupee-sign fa-4x text-primary"></i>
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">Total Withdrawals</h5>
                                        <h3 className="mb-3">Rs. {earningsData.total_withdrawals}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 wow fadeInUp" data-wow-delay="0.1s">
                            <div className="row-cols-1 feature-item p-4 rounded" style={{border: '1px solid var(--bs-primary)'}}>
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <i className="fas fa-rupee-sign fa-4x text-primary"></i>
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">Last 7 Days of Earnings</h5>
                                        {earningsData?.weekly_earnings.map((value, index) => (
                                            <p className="py-3 px-2 border border-primary rounded" key={index}>
                                                <b>{moment(value.date).format('ddd, D MMM YYYY')}</b> <span className="float-end"><b>Rs. {value.amount}</b></span>
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 wow fadeInUp" data-wow-delay="0.1s">
                            <div className="row-cols-1 feature-item p-4 rounded" style={{border: '1px solid var(--bs-primary)'}}>
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <i className="fas fa-rupee-sign fa-4x text-primary"></i>
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">Selected Date Earnings</h5>
                                        <DateRangePicker 
                                            className="form-control py-3 border-primary bg-transparent mb-3 text-center"
                                            onChange={(e) => { setSelectDate(e); }} 
                                            value={selectDate} 
                                            format="dd/MM/yyyy"
                                        />
                                        <hr />
                                        {earningsData?.filtered_earnings.length == 0 && (
                                            <p className="py-3 px-2 border border-primary rounded">
                                                <b>No Earnings Data Found</b>
                                            </p>
                                        )}
                                        {earningsData?.filtered_earnings.map((value, index) => (
                                            <p className="py-3 px-2 border border-primary rounded" key={index}>
                                                <b>{moment(value.date).format('ddd, D MMM YYYY')}</b> <span className="float-end"><b>Rs. {value.amount}</b></span>
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 wow fadeInUp" data-wow-delay="0.1s">
                            <div className="row-cols-1 feature-item p-4 rounded" style={{border: '1px solid var(--bs-primary)'}}>
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <i className="fas fa-rupee-sign fa-4x text-primary"></i>
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-4">Last 7 Withdrawals</h5>
                                        {earningsData?.last_7_withdrawals.map((value, index) => (
                                            <p className="py-3 px-2 border border-primary rounded" key={index}>
                                                <b>{index+1}</b>. <b>Rs. {value.amount}</b> on <b>{value.withdrawn_at}, Via {value.payment_method} ({value.transaction_id}), {value.status || 'Pending'}</b>
                                                {value.invoice_url && <span className="float-end" title="View Invoice">
                                                    <i className="fas fa-eye text-primary me-3" style={{cursor: 'pointer'}} onClick={() => viewFileViewFinder(value.invoice_url)}></i>
                                                </span>}
                                            </p>
                                        ))}
                                        {earningsData?.last_7_withdrawals.length === 0 && (
                                            <p className="py-3 px-2 border border-primary rounded">
                                                <b>No Withdrawals Data Found</b>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 wow fadeInUp" data-wow-delay="0.1s">
                            <div className="row-cols-1 feature-item p-4 rounded" style={{border: '1px solid var(--bs-primary)'}}>
                                <div className="col-12">
                                    <div className="feature-icon mb-4">
                                        <div className="p-3 d-inline-flex bg-white rounded">
                                            <i className="fas fa-rupee-sign fa-4x text-primary"></i>
                                        </div>
                                    </div>
                                    <div className="feature-content d-flex flex-column">
                                        <h5 className="mb-1">Request Withdrawals</h5>
                                        <hr />
                                        <form>
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="withdrawal_amount" className="form-label">Amount</label>
                                                        <input className="form-control border-primary" id="withdrawal_amount" value={withdrawalFormData?.withdrawal_amount} onChange={(e) => setWithdrawalFormData({ ...withdrawalFormData, withdrawal_amount: e.target.value })} />
                                                        {withdrawalFormError?.chief_complaints && <div className="text-danger">{withdrawalFormError?.chief_complaints}</div>}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-3">
                                                        <label htmlFor="remarks" className="form-label">Payment Mode</label>
                                                        <div className="input-group mb-3">
                                                            <select 
                                                                className="form-select border-primary" 
                                                                name="withdrawal_payment_mode" 
                                                                value={withdrawalFormData?.withdrawal_payment_mode}
                                                                onChange={(e) => setWithdrawalFormData({ ...withdrawalFormData, withdrawal_payment_mode: e.target.value })}
                                                            >
                                                                <option value="">Select an option</option>
                                                                {['Cash','UPI','Bank Transfer'].map((item) => (
                                                                    <option key={item} value={item}>{item}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        {withdrawalFormError?.is_completed && <div className="text-danger">{withdrawalFormError?.is_completed}</div>}
                                                    </div>
                                                </div>
                                            </div>
                                            <button type="button" className="btn btn-primary float-end mt-5" onClick={handleWithdrawalRequest}>Submit</button>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default DoctorReports;